import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  Toast,
  TOAST_VARIANTS,
  TableContainer,
  TableHeader,
  TableContent,
  TOAST_VISIBILITY_DURATIONS,
  CELL_RENDERERS,
  usePreventTableScrollingRef,
  TABLE_LAYOUT_OPTIONS,
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import LoadingOverlaySpinner from '../../../../loader/LoadingOverlaySpinner';

export default function ReviewFields() {
  const state = useSelector((state) => state);
  const { verticalScopingApisList } = state && state?.selectDMSSlice;
  const {
    selectedLocationName,
    currentFieldSelection,
    updatedFieldSelection,
    savingSubscriptionFields,
  } = state?.apiFieldsSlice;
  const [disabledRequiredFields, setDisabledRequiredFields] = useState(0);

  const columns = [
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'location',
      tooltipField: 'location',
      headerName: 'Dealership Location',
      width: 400,
      maxWidth: 400,
      lockPosition: true,
      resizable: true,
      cellClass:'table-cell-ellipsis'
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'api',
      tooltipField: 'api',
      headerName: 'API',
      width: 400,
      maxWidth: 400,
      lockPosition: true,
      resizable: true,
      cellClass:'table-cell-ellipsis'
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'field',
      headerName: 'Fields',
      width: 520,
      maxWidth: 520,
      lockPosition: true,
      resizable: true,
      tooltipField: 'field',
      cellClass:'table-cell-ellipsis'
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'status',
      tooltipField: 'status',
      headerName: 'Status',
      width: 120,
      maxWidth: 120,
      lockPosition: true,
      resizable: true,
    },
  ];

  const [data, setData] = useState([]);
  const tableContentRef = usePreventTableScrollingRef();
  const gridRef = useRef();
  const headerProps = {
    headerTitle: 'Review API Fields',
    id: `table-header`,
    isSearchable: false,
    isDownloadable: false,
    isPrintable: false,
    isAlwaysExpanded: false,
  };

  useEffect(() => {
    const tableData = [];
    const result = {};
    let disabledRequiredFieldCount = 0;
    verticalScopingApisList
      ?.forEach((api) => {
        if (api?.apiFieldSelections?.instances) {
          let apiFields = Object.values(api.apiFieldSelections.instances)
            ?.flat()
            ?.filter((field) => field?.required)
            ?.map((f) => f?.name);
          const uniqueRequiredFields = Array.from(new Set(apiFields));
          uniqueRequiredFields.forEach((field) => {
            if (!updatedFieldSelection[api.id]?.includes(field))
              disabledRequiredFieldCount += 1;
          });
        }
      });

    for (const key in currentFieldSelection) {
      result[key] = currentFieldSelection[key]
        ?.map((field) => {
          if (!updatedFieldSelection[key]?.includes(field)) {
            return { name: field, status: 'Disabled' };
          }
        })
        ?.filter(Boolean);
    }

    for (const key in updatedFieldSelection) {
      if (!result[key]) {
        result[key] = [];
      }

      const temp = updatedFieldSelection[key]
        .map((field) => {
          if (
            !currentFieldSelection[key] ||
            !currentFieldSelection[key]?.includes(field)
          ) {
            return { name: field, status: 'Enabled' };
          }
        })
        .filter(Boolean);
      result[key] = result[key].concat(temp);
    }
    Object.keys(result).forEach((api) => {
      const apiInfo = verticalScopingApisList?.filter(
        (vsApi) => vsApi.id === api
      );
      result[api]?.forEach((field) => {
        tableData.push({
          location: selectedLocationName,
          api: apiInfo[0]?.name,
          field: field?.name,
          status: field?.status,
        });
      });
    });
    setData(tableData);
    setDisabledRequiredFields(disabledRequiredFieldCount);
  }, []);

  return (
    <div className="review-fields-div">
      {savingSubscriptionFields && (
        <LoadingOverlaySpinner active={true} text="Saving Fields" />
      )}
      {!!disabledRequiredFields && (
        <Toast
          content={`You have disabled ${disabledRequiredFields} required field${
            disabledRequiredFields > 1 ? '(s)' : ''
          }. This might cause your app to no longer function as intended.`}
          id="toast-1"
          visibilityDuration={TOAST_VISIBILITY_DURATIONS.INFINITE}
          variant={TOAST_VARIANTS.WARNING}
          style={{ paddingBottom: '15px', zIndex: '1' }}
        />
      )}

      <TableContainer
        id="ag-grid-container"
        className={classNames('edit-fields-locations-table', 'ag-theme-balham')}
      >
        <TableHeader {...headerProps} />
        <TableContent
          data-testid="table-content"
          className="edit-fields-locations-table-content"
          ref={tableContentRef}
        >
          <AgGridReact
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50, 100]}
            columnDefs={columns}
            domLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
            // onGridReady={handleGridReady}
            rowData={data}
            rowHeight={50}
            style={{ width: '100%' }}
            ref={gridRef}
          />
        </TableContent>
      </TableContainer>
    </div>
  );
}
